import { getIsPriv } from 'config';
import { getRuntimeEnv } from 'util/env';
import {
  DEFAULT_POINT_HUB_MAX_PAYLOAD_BYTES,
  DEFAULT_POINT_HUB_SOURCE_NAME,
  DEFAULT_POINT_HUB_SOURCE_TYPE
} from './constants';
import type { AIRecommendPlatform, PointHubConfig } from './types';

interface PointHubRuntimeConfig {
  enabled?: boolean;
  endpoint?: string;
  appId?: string;
  appSecret?: string;
  environment?: string;
  sourceName?: string;
  sourceType?: AIRecommendPlatform;
  debug?: boolean;
  maxPayloadBytes?: number;
}

interface PointHubEnvironmentDefaults {
  enabled: boolean;
  endpoint: string;
  appId: string;
  appSecret: string;
}

declare global {
  interface Window {
    __POINT_HUB_CONFIG__?: PointHubRuntimeConfig;
  }
}

const LOCAL_POINT_HUB_DEFAULTS: PointHubEnvironmentDefaults = {
  enabled: true,
  endpoint: 'http://127.0.0.1:15688',
  appId: 'app_52ef91b6aecf7996acbde8c6',
  appSecret: 'sec_b9e5f8a7447815117fa656f544910022'
};

const SHARED_POINT_HUB_DEFAULTS: PointHubEnvironmentDefaults = {
  enabled: true,
  endpoint: 'https://phb.test.maxhub.vip',
  appId: 'app_e03b0b9eca902bd23e4e8b0b',
  appSecret: 'sec_5c803dc089556953eba2e6c46e3e9ef3'
};

const DISABLED_POINT_HUB_DEFAULTS: PointHubEnvironmentDefaults = {
  enabled: false,
  endpoint: '',
  appId: '',
  appSecret: ''
};

const parseBoolean = (value: unknown): boolean => value === true || String(value).toLowerCase() === 'true';

const parseOptionalBoolean = (value: unknown): boolean | undefined => {
  if (value === undefined) return undefined;
  return parseBoolean(value);
};

const parsePositiveInteger = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseSourceType = (value: unknown): AIRecommendPlatform => {
  return value === 'h5' || value === 'miniprogram' || value === 'web' ? value : DEFAULT_POINT_HUB_SOURCE_TYPE;
};

const readRuntimeConfig = (): PointHubRuntimeConfig => {
  return typeof window === 'undefined' ? {} : window.__POINT_HUB_CONFIG__ || {};
};

const readBuildConfig = (): PointHubRuntimeConfig => ({
  enabled: parseOptionalBoolean(process.env.REACT_APP_POINT_HUB_ENABLED || process.env.POINT_HUB_ENABLED),
  endpoint: process.env.REACT_APP_POINT_HUB_ENDPOINT || process.env.POINT_HUB_ENDPOINT,
  appId: process.env.REACT_APP_POINT_HUB_APP_ID || process.env.POINT_HUB_APP_ID,
  appSecret: process.env.REACT_APP_POINT_HUB_APP_SECRET || process.env.POINT_HUB_APP_SECRET,
  environment: process.env.REACT_APP_POINT_HUB_ENVIRONMENT || process.env.POINT_HUB_ENVIRONMENT,
  sourceName: process.env.REACT_APP_POINT_HUB_SOURCE_NAME || process.env.POINT_HUB_SOURCE_NAME,
  sourceType: parseSourceType(process.env.REACT_APP_POINT_HUB_SOURCE_TYPE),
  debug: parseBoolean(process.env.REACT_APP_POINT_HUB_DEBUG || process.env.POINT_HUB_DEBUG),
  maxPayloadBytes: parsePositiveInteger(
    process.env.REACT_APP_POINT_HUB_MAX_PAYLOAD_BYTES || process.env.POINT_HUB_MAX_PAYLOAD_BYTES
  )
});

const pick = <T>(runtimeValue: T | undefined, buildValue: T | undefined, fallback: T): T => {
  if (runtimeValue !== undefined) return runtimeValue;
  if (buildValue !== undefined) return buildValue;
  return fallback;
};

const getDefaultRuntimeEnvironment = (): string => {
  return typeof window === 'undefined' ? '' : getRuntimeEnv().toLowerCase();
};

const getEnvironmentDefaults = (environment: string, privateDeployment: boolean): PointHubEnvironmentDefaults => {
  if (privateDeployment || environment === 'priv') return DISABLED_POINT_HUB_DEFAULTS;
  if (environment === 'local') return LOCAL_POINT_HUB_DEFAULTS;
  if (environment === 'dev' || environment === 'test' || environment === 'prod' || environment === 'pro') {
    return SHARED_POINT_HUB_DEFAULTS;
  }
  return DISABLED_POINT_HUB_DEFAULTS;
};

export const getPointHubConfig = (): PointHubConfig => {
  const runtime = readRuntimeConfig();
  const build = readBuildConfig();
  const privateDeployment = getIsPriv();
  const environment = pick(runtime.environment, build.environment, getDefaultRuntimeEnvironment());
  const environmentDefaults = getEnvironmentDefaults(environment, privateDeployment);
  const enabled = pick(runtime.enabled, build.enabled, environmentDefaults.enabled);

  return {
    enabled: privateDeployment && runtime.enabled !== true ? false : enabled,
    endpoint: pick(runtime.endpoint, build.endpoint, environmentDefaults.endpoint),
    appId: pick(runtime.appId, build.appId, environmentDefaults.appId),
    appSecret: pick(runtime.appSecret, build.appSecret, environmentDefaults.appSecret),
    environment,
    sourceName: pick(runtime.sourceName, build.sourceName, DEFAULT_POINT_HUB_SOURCE_NAME),
    sourceType: parseSourceType(pick(runtime.sourceType, build.sourceType, DEFAULT_POINT_HUB_SOURCE_TYPE)),
    debug: pick(runtime.debug, build.debug, false),
    maxPayloadBytes: parsePositiveInteger(
      pick(runtime.maxPayloadBytes, build.maxPayloadBytes, DEFAULT_POINT_HUB_MAX_PAYLOAD_BYTES)
    )
  };
};

export const getPointHubConfigValidationErrors = (config: PointHubConfig): string[] => {
  if (!config.enabled) return [];
  const errors: string[] = [];
  if (!config.endpoint) errors.push('endpoint');
  if (!config.appId) errors.push('appId');
  if (!config.appSecret) errors.push('appSecret');
  if (!config.environment) errors.push('environment');
  if (!config.sourceName) errors.push('sourceName');
  if (!config.maxPayloadBytes) errors.push('maxPayloadBytes');
  return errors;
};

export const isPointHubConfigReady = (config: PointHubConfig = getPointHubConfig()): boolean => {
  return config.enabled && getPointHubConfigValidationErrors(config).length === 0;
};
